import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bookmark, Copy, Loader2, Minus, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ResponsiveFormDialog from "@/components/ResponsiveFormDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addItemId, setAddItemId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { addLines } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = () => api.get("/templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(errMsg(e)));

  useEffect(() => {
    load();
    api.get("/items").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const isOwner = (t) => t.owner_id === user?.id || user?.role === "admin";
  const canEdit = (t) => isOwner(t) || t.is_shared;
  const grouped = useMemo(() => {
    const g = {};
    (editing?.lines || []).forEach((l) => { (g[l.category || "Lainnya"] = g[l.category || "Lainnya"] || []).push(l); });
    return g;
  }, [editing]);

  const setLineQty = (itemId, qty) =>
    setEditing((prev) => ({
      ...prev,
      lines: prev.lines.map((l) => (l.item_id === itemId ? { ...l, qty: Math.max(1, qty) } : l)),
    }));

  const removeLine = (itemId) =>
    setEditing((prev) => ({ ...prev, lines: prev.lines.filter((l) => l.item_id !== itemId) }));

  const addLine = () => {
    const item = items.find((i) => i.id === addItemId);
    if (!item) return;
    if (editing.lines.some((l) => l.item_id === item.id)) return toast.error("Alat sudah ada di paket");
    setEditing((prev) => ({
      ...prev,
      lines: [...prev.lines, {
        item_id: item.id, name: item.name, item_code: item.item_code,
        category: item.category, qty: 1, photo: item.photo, max: item.quantity,
      }],
    }));
    setAddItemId("");
  };

  const save = async () => {
    if (!editing.lines.length) return toast.error("Paket harus punya minimal satu alat");
    if (!editing.name?.trim()) return toast.error("Nama paket wajib diisi");
    setBusy(true);
    const body = {
      name: editing.name,
      is_shared: !!editing.is_shared,
      lines: editing.lines.map((l) => ({ item_id: l.item_id, qty: l.qty })),
    };
    try {
      if (editing.id) await api.put(`/templates/${editing.id}`, body);
      else await api.post("/templates", body);
      toast.success(editing.id ? "Paket diperbarui" : "Paket dibuat");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const hapus = async (t) => {
    setBusy(true);
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success("Paket dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      setDeleteTarget(null);
    }
  };

  return (
    <div className="space-y-6 pb-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-[26px] font-semibold tracking-tight text-slate-900">Template Paket</h1>
          <p className="mt-1.5 max-w-[70ch] text-sm text-slate-500">
            Simpan kombinasi alat favorit, edit kapan saja, dan pakai sekali klik saat booking.
          </p>
        </div>
        <Button data-testid="new-template-button"
          onClick={() => setEditing({ name: "", is_shared: false, lines: [] })}
          className="h-[44px] rounded-lg border border-[#0067c8] bg-[#0067c8] px-5 font-semibold text-white hover:border-[#004f9e] hover:bg-[#004f9e]">
          <Plus className="mr-2 h-4 w-4" /> Buat Paket Baru
        </Button>
      </div>

      <div data-testid="templates-page-list" className="grid gap-4 md:grid-cols-2">
        {templates.map((t) => (
          <div key={t.id} data-testid={`template-card-${t.id}`}
            className="space-y-4 rounded-lg border border-slate-200 bg-white p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="flex items-center gap-2 font-heading text-xl font-semibold tracking-tight text-slate-900">
                  <Bookmark className="h-4 w-4 text-[#0067c8]" /> {t.name}
                </h2>
                <p className="mt-1 text-xs text-slate-500">
                  {t.lines.length} jenis · {t.lines.reduce((s, l) => s + l.qty, 0)} pcs · milik {t.owner_name}
                  {t.is_shared ? " · dibagikan" : ""}
                </p>
              </div>
              <div className="flex gap-1">
                <button data-testid={`duplicate-template-${t.id}`} title="Duplikat jadi paket saya"
                  onClick={() => setEditing({ name: `${t.name} (copy)`, is_shared: false, lines: [...t.lines] })}
                  className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                  <Copy className="h-4 w-4" />
                </button>
                {canEdit(t) && (
                  <button data-testid={`edit-template-${t.id}`} onClick={() => setEditing({ ...t, lines: [...t.lines] })}
                    className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900">
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
                {isOwner(t) && (
                  <button data-testid={`delete-template-${t.id}`} onClick={() => setDeleteTarget(t)}
                    className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-rose-700">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              {t.lines.map((l) => (
                <div key={l.item_id} className="flex justify-between gap-3 rounded-lg bg-slate-100 px-4 py-2 text-sm text-slate-900">
                  <span className="truncate">{l.name}</span>
                  <span className="font-mono text-xs tabular-nums text-slate-500">{l.qty} pcs</span>
                </div>
              ))}
            </div>

            <Button data-testid={`use-template-page-${t.id}`}
              onClick={() => { addLines(t.lines); toast.success("Paket masuk keranjang"); navigate("/checkout"); }}
              className="h-[44px] w-full rounded-lg border border-[#0067c8] bg-[#0067c8] font-semibold text-white hover:border-[#004f9e] hover:bg-[#004f9e]">
              Pakai Paket Ini
            </Button>
          </div>
        ))}
        {templates.length === 0 && (
          <p className="text-sm text-slate-500">
            Belum ada paket. Klik "Buat Paket Baru" di atas, atau tambahkan alat ke keranjang lalu simpan sebagai paket di halaman Konfirmasi Booking.
          </p>
        )}
      </div>

      <ResponsiveFormDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}
        tone="light" title={`Hapus paket "${deleteTarget?.name}"?`}
        description="Paket dihapus permanen dan tidak bisa dikembalikan. Isi keranjang tidak ikut berubah.">
        <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
          <Button data-testid="cancel-delete-template-button" autoFocus variant="outline"
            onClick={() => setDeleteTarget(null)}
            className="h-12 rounded-full border-slate-300 bg-white px-6 text-slate-900 hover:border-slate-900">
            Batal
          </Button>
          <Button data-testid="confirm-delete-template-button" disabled={busy}
            onClick={() => deleteTarget && hapus(deleteTarget)}
            className="h-12 rounded-full bg-rose-600 px-6 font-semibold text-white hover:bg-rose-700">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Ya, hapus"}
          </Button>
        </div>
      </ResponsiveFormDialog>

      <ResponsiveFormDialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}
        tone="light" title={editing?.id ? "Edit Paket" : "Buat Paket Baru"}
        description="Ubah nama, jumlah tiap alat, tambah atau hapus alat dari paket ini.">
        {editing && (
            <div className="space-y-5 text-slate-900">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Nama Paket</Label>
                <Input data-testid="edit-template-name-input" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="h-[44px] rounded-lg border-slate-300 bg-white text-slate-900" />
              </div>

              <label className="flex items-center gap-3 text-sm text-slate-600">
                <input data-testid="edit-template-shared-checkbox" type="checkbox" checked={!!editing.is_shared}
                  onChange={(e) => setEditing({ ...editing, is_shared: e.target.checked })}
                  className="h-4 w-4 accent-[#0067c8]" />
                Bagikan ke semua orang
              </label>

              <div className="space-y-4">
                {Object.entries(grouped).map(([cat, list]) => (
                  <div key={cat} className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{cat}</p>
                    {list.map((l) => (
                      <div key={l.item_id} data-testid={`edit-line-${l.item_code}`}
                        className="flex items-center justify-between gap-3 rounded-xl bg-slate-100 px-4 py-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm">{l.name}</p>
                          <p className="font-mono text-xs text-slate-500">{l.item_code} · stok {l.max}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <button data-testid={`edit-dec-${l.item_code}`} onClick={() => setLineQty(l.item_id, l.qty - 1)}
                            className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-slate-700 hover:bg-slate-200"><Minus className="h-3.5 w-3.5" /></button>
                          <span className="min-w-10 text-center font-mono text-sm tabular-nums">{l.qty}</span>
                          <button data-testid={`edit-inc-${l.item_code}`} disabled={l.qty >= (l.max || 99)}
                            onClick={() => setLineQty(l.item_id, l.qty + 1)}
                            className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-slate-700 hover:bg-slate-200 disabled:opacity-30"><Plus className="h-3.5 w-3.5" /></button>
                          <button data-testid={`edit-remove-${l.item_code}`} onClick={() => removeLine(l.item_id)}
                            className="min-h-[44px] min-w-[44px] rounded-lg p-2 text-slate-500 hover:bg-slate-200 hover:text-rose-700"><X className="h-3.5 w-3.5" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Tambah Alat</Label>
                <div className="flex gap-2">
                  <Select value={addItemId} onValueChange={setAddItemId}>
                    <SelectTrigger data-testid="edit-add-item-select" className="h-[44px] flex-1 rounded-lg border-slate-300 bg-white text-slate-900">
                      <SelectValue placeholder="Pilih alat" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {items.map((i) => (
                        <SelectItem key={i.id} value={i.id}>{i.name} ({i.quantity} pcs)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button data-testid="edit-add-item-button" onClick={addLine} disabled={!addItemId}
                    className="h-[44px] rounded-lg border border-slate-300 bg-white px-5 font-semibold text-slate-900 hover:border-slate-900">
                    Tambah
                  </Button>
                </div>
              </div>

              <Button data-testid="save-template-edit-button" onClick={save} disabled={busy}
                className="h-[44px] w-full rounded-lg border border-[#0067c8] bg-[#0067c8] font-semibold text-white hover:border-[#004f9e] hover:bg-[#004f9e]">
                {editing.id ? "Simpan Perubahan" : "Simpan Paket"}
              </Button>
            </div>
          )}
      </ResponsiveFormDialog>
    </div>
  );
}
